import { useMemo } from 'react';
import { useSite } from 'src/context/providers/SiteProvider';
import {
  openDrawer,
  closeDrawer,
  toggleDrawer,
  closeAllDrawers,
  closeAllButCurrent,
  reopenLastDrawer,
  setLastOpenDrawerId,
  DrawerState, // Import the state type
} from 'src/context/reducers/drawerSlice';

/**
 * A custom hook that provides access to drawer state and actions
 * (Using useSite custom hook)
 *
 * The action dispatchers keep their identity across renders; the state
 * selectors change only when the drawer state does.
 *
 * @returns An object with drawer state selectors and action dispatchers
 */
export const useDrawer = () => {
  // Use the custom hook
  const { state, dispatch } = useSite();

  const drawerState: DrawerState | undefined = state?.drawers;

  const actions = useMemo(
    () => ({
      openDrawer: (id: string) => dispatch(openDrawer({ id })),
      closeDrawer: (id: string) => dispatch(closeDrawer({ id })),
      toggleDrawer: (id: string) => dispatch(toggleDrawer({ id })),
      closeAllDrawers: () => dispatch(closeAllDrawers()),
      closeAllButCurrent: (id: string) => dispatch(closeAllButCurrent({ id })),
      reopenLastDrawer: () => dispatch(reopenLastDrawer()),
      setLastOpenDrawerId: (id: string) => dispatch(setLastOpenDrawerId({ id })),
    }),
    [dispatch]
  );

  return useMemo(
    () => ({
      // Action dispatchers
      ...actions,
      // State selectors
      isDrawerOpen: (id: string): boolean => {
        const drawer = drawerState?.[id];
        return Boolean(drawer && typeof drawer !== 'string' && drawer.isOpen);
      },
      // Get the last open drawer ID (current drawer)
      getLastOpenDrawerId: (): string | undefined => {
        return drawerState?.lastOpenDrawerId;
      },
      // Get the previous drawer ID (the one before the current)
      getPreviousDrawerId: (): string | undefined => {
        return drawerState?.previousDrawerId;
      },
    }),
    [actions, drawerState]
  );
};
